import { useState, useMemo } from 'react'
import { showSuccess, showError } from '../utils/toast'

// ── Mock data ───────────────────────────────────────────────────────────────
const initialAgents = [
  { id: 'u1', initials: 'SJ', name: 'Sarah Johnson',   role: 'Account Executive', color: 'bg-blue-100 text-blue-700',    enabled: true,  linked: 8,  lastActive: '2h ago',     sfActive: true  },
  { id: 'u2', initials: 'MC', name: 'Mike Chen',       role: 'Sales Rep',         color: 'bg-purple-100 text-purple-700',enabled: true,  linked: 3,  lastActive: 'Yesterday',  sfActive: true  },
  { id: 'u3', initials: 'ER', name: 'Emily Rodriguez', role: 'SDR',               color: 'bg-green-100 text-green-700',  enabled: true,  linked: 5,  lastActive: 'Yesterday',  sfActive: true  },
  { id: 'u4', initials: 'DP', name: 'David Park',      role: 'Account Executive', color: 'bg-amber-100 text-amber-700',  enabled: true,  linked: 2,  lastActive: '4 days ago', sfActive: true  },
  { id: 'u5', initials: 'LT', name: 'Lisa Thompson',   role: 'Sales Rep',         color: 'bg-pink-100 text-pink-700',    enabled: false, linked: 0,  lastActive: null,         sfActive: true  },
  { id: 'u6', initials: 'JW', name: 'James Wilson',    role: 'SDR',               color: 'bg-teal-100 text-teal-700',    enabled: false, linked: 0,  lastActive: null,         sfActive: true  },
  { id: 'u7', initials: 'RG', name: 'Rachel Green',    role: 'Account Executive', color: 'bg-indigo-100 text-indigo-700',enabled: false, linked: 0,  lastActive: null,         sfActive: true  },
  { id: 'u8', initials: 'TM', name: 'Tom Martinez',    role: 'Sales Rep',         color: 'bg-orange-100 text-orange-700',enabled: false, linked: 0,  lastActive: null,         sfActive: true  },
  { id: 'u9', initials: 'AF', name: 'Amanda Foster',   role: 'SDR',               color: 'bg-cyan-100 text-cyan-700',    enabled: false, linked: 0,  lastActive: null,         sfActive: true  },
  { id: 'u10',initials: 'KO', name: "Kevin O'Brien",   role: 'Senior AE',         color: 'bg-violet-100 text-violet-700',enabled: false, linked: 0,  lastActive: null,         sfActive: true  },
]

const recencyScore = (s) => {
  if (!s) return Infinity
  const h = s.match(/^(\d+)h ago$/);   if (h) return parseInt(h[1]) / 24
  if (s === 'Yesterday') return 1
  const d = s.match(/^(\d+) days? ago$/); if (d) return parseInt(d[1])
  return 0.1
}


// ── Toggle switch ───────────────────────────────────────────────────────────
function Toggle({ enabled, onChange }) {
  return (
    <button
      onClick={onChange}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
        enabled ? 'bg-blue-600' : 'bg-gray-200'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow ring-0 transition-transform duration-200 ${
          enabled ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </button>
  )
}

// ── Section wrapper ─────────────────────────────────────────────────────────
function Section({ title, description, action, children }) {
  return (
    <div className="card overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-800">{title}</div>
          {description && <div className="text-xs text-gray-400 mt-0.5">{description}</div>}
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </div>
      <div className="p-5">{children}</div>
    </div>
  )
}

// ── Connection status row ───────────────────────────────────────────────────
function ConnectionRow({ label, value, sub, status }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
      <div className="text-xs text-gray-500 font-medium w-36 flex-shrink-0">{label}</div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-gray-800 truncate">{value}</div>
        {sub && <div className="text-xs text-gray-400 mt-0.5 font-mono truncate">{sub}</div>}
      </div>
      {status === 'connected' && (
        <div className="flex items-center gap-1 ml-4 flex-shrink-0">
          <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
          <span className="text-xs text-green-600 font-medium">Connected</span>
        </div>
      )}
    </div>
  )
}

// ── Settings ────────────────────────────────────────────────────────────────
export default function Settings() {
  const [agents, setAgents] = useState(initialAgents)
  const [sortBy, setSortBy] = useState('status')
  const [search, setSearch] = useState('')
  const [refreshState, setRefreshState] = useState('idle')
  const [lastRefreshed, setLastRefreshed] = useState(null)

  const toggleAgent = (id) => {
    try {
      setAgents(prev => prev.map(a => a.id === id ? { ...a, enabled: !a.enabled } : a))
      showSuccess('Agent access updated')
    } catch (err) {
      console.error('Toggle agent failed:', err)
      showError(err?.message ?? 'Failed to update agent')
    }
  }

  const handleRefresh = async () => {
    setRefreshState('refreshing')
    try {
      await new Promise((r) => setTimeout(r, 1500))
      setAgents(prev => prev.map(a => (a.id === 'u4' ? { ...a, sfActive: false } : a)))
      setRefreshState('done')
      setLastRefreshed('just now')
      showSuccess('Refreshed from Salesforce')
      setTimeout(() => setRefreshState('idle'), 4000)
    } catch (err) {
      console.error('Refresh failed:', err)
      setRefreshState('idle')
      showError(err?.message ?? 'Failed to refresh')
    }
  }

  const enabledCount = agents.filter(a => a.enabled).length

  const visibleAgents = useMemo(() => {
    // Sort first
    const arr = [...agents]
    switch (sortBy) {
      case 'name':
        arr.sort((a, b) => a.name.localeCompare(b.name)); break
      case 'role':
        arr.sort((a, b) => a.role.localeCompare(b.role) || a.name.localeCompare(b.name)); break
      case 'links':
        arr.sort((a, b) => b.linked - a.linked || a.name.localeCompare(b.name)); break
      default: // status
        arr.sort((a, b) => {
          if (a.enabled !== b.enabled) return a.enabled ? -1 : 1
          return recencyScore(a.lastActive) - recencyScore(b.lastActive)
        })
    }
    // Then filter
    if (!search) return arr
    const q = search.toLowerCase()
    return arr.filter(a => a.name.toLowerCase().includes(q) || a.role.toLowerCase().includes(q))
  }, [agents, sortBy, search])

  const refreshAction = (
    <div className="flex items-center gap-3">
      {lastRefreshed && <span className="text-xs text-gray-400">Updated {lastRefreshed}</span>}
      <button
        onClick={handleRefresh}
        disabled={refreshState === 'refreshing'}
        className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
      >
        <svg
          className={refreshState === 'refreshing' ? 'animate-spin' : ''}
          width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M3 22v-6h6"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
        </svg>
        {refreshState === 'refreshing' ? 'Refreshing…' : 'Refresh from Salesforce'}
      </button>
    </div>
  )

  return (
    <div className="min-h-full bg-gray-50 px-4 py-10">
      <div className="max-w-3xl mx-auto mb-6">
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
          <span>BillJean</span><span>›</span><span>Settings</span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage your Stripe connection and agent access.
        </p>
      </div>

      <div className="max-w-3xl mx-auto space-y-5">

        {/* Stripe Connection */}
        <Section
          title="Stripe Connection"
          description="Your connected Stripe account and sync status."
        >
          <ConnectionRow label="Account name" value="Acme Corp"              status="connected" />
          <ConnectionRow label="Account ID"   value="acct_1BXFHjKHe5BvZkAE" sub="acct_1BXFHjKHe5BvZkAE" />
          <ConnectionRow label="Last synced"  value="5 minutes ago" />
          <ConnectionRow label="Mode"         value="Live" />
          <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end">
            <button className="text-xs text-red-400 hover:text-red-600 transition-colors font-medium">
              Disconnect Stripe
            </button>
          </div>
        </Section>

        {/* Agents */}
        <Section
          title={`Agents (${enabledCount} of ${agents.length} enabled)`}
          description="Sales reps with access to the BillJean widget inside Salesforce."
          action={refreshAction}
        >
          {/* Search + filter toolbar */}
          <div className="space-y-2.5 mb-3">
            {/* Search */}
            <div className="relative">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                placeholder="Search by name or role…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
              />
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 cursor-pointer"
            >
              <option value="status">Status: Enabled first</option>
              <option value="name">Name: A → Z</option>
              <option value="role">Role</option>
              <option value="links">Most links</option>
            </select>

            {/* Result count when filtering */}
            {search && (
              <p className="text-xs text-gray-400">
                Showing {visibleAgents.length} of {agents.length} users matching <span className="font-medium text-gray-600">"{search}"</span>
                <button onClick={() => setSearch('')} className="ml-2 text-blue-500 hover:text-blue-700">Clear</button>
              </p>
            )}
          </div>

          {/* Agent list */}
          <div className="divide-y divide-gray-50 -mx-5 -mb-5 border-t border-gray-100">
            {visibleAgents.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-gray-400">
                No users match your search.
              </div>
            ) : visibleAgents.map(agent => {
              const isFlagged = agent.enabled && !agent.sfActive
              return (
                <div
                  key={agent.id}
                  className={`flex items-center gap-3 px-5 py-3.5 transition-colors ${
                    isFlagged ? 'bg-amber-50/50' : agent.enabled ? 'bg-white' : 'bg-gray-50'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    agent.enabled ? agent.color : 'bg-gray-100 text-gray-400'
                  }`}>
                    {agent.initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-medium ${agent.enabled ? 'text-gray-900' : 'text-gray-400'}`}>
                        {agent.name}
                      </span>
                      {isFlagged && (
                        <span className="badge bg-amber-100 text-amber-700 border border-amber-200 text-xs flex-shrink-0">
                          SF inactive
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400">{agent.role}</div>
                  </div>
                  <div className="text-right mr-4 flex-shrink-0">
                    {agent.enabled ? (
                      <>
                        <div className="text-xs font-semibold text-gray-700">{agent.linked} links</div>
                        <div className="text-xs text-gray-400">{agent.lastActive ?? '—'}</div>
                      </>
                    ) : (
                      <div className="text-xs text-gray-300">No access</div>
                    )}
                  </div>
                  <Toggle enabled={agent.enabled} onChange={() => toggleAgent(agent.id)} />
                </div>
              )
            })}
          </div>
        </Section>

      </div>
    </div>
  )
}
